import React, { useEffect, useRef, useState } from 'react';
import { Heading2, Heading3, Bold, Link2, List, Loader2, ImagePlus, Trash2, Eye, EyeOff, Plus } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { AdminGate, adminFetch } from '../components/admin/AdminGate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { title: '', excerpt: '', body: '', cover_path: '', published: false };
export const fileUrl = (path) => (path ? `${API}/files/${path}` : '');

const TOOLS = [
  { icon: Heading2, label: 'Heading', wrap: ['\n## ', ''] },
  { icon: Heading3, label: 'Subheading', wrap: ['\n### ', ''] },
  { icon: Bold, label: 'Bold', wrap: ['**', '**'] },
  { icon: List, label: 'Bullet', wrap: ['\n- ', ''] },
  { icon: Link2, label: 'Link', wrap: ['[', '](https://)'] },
];

function Editor({ post, onSaved, onCancel }) {
  const [form, setForm] = useState(post || EMPTY);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [preview, setPreview] = useState(false);
  const ta = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const insert = ([a, b]) => {
    const el = ta.current;
    const { selectionStart: s, selectionEnd: e } = el;
    const sel = form.body.slice(s, e) || (b === '](https://)' ? 'link text' : '');
    set('body', form.body.slice(0, s) + a + sel + b + form.body.slice(e));
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = s + a.length + sel.length; }, 0);
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy('upload'); setErr('');
    const fd = new FormData(); fd.append('file', file);
    const r = await adminFetch('/admin/upload', { method: 'POST', body: fd });
    const d = await r.json();
    setBusy('');
    if (!r.ok) { setErr(d.detail || 'Upload failed'); return; }
    set('cover_path', d.path);
  };

  const save = async (published) => {
    setBusy('save'); setErr('');
    const body = JSON.stringify({ ...form, published });
    const r = post?.id ? await adminFetch(`/admin/blog/${post.id}`, { method: 'PUT', body }) : await adminFetch('/admin/blog', { method: 'POST', body });
    setBusy('');
    if (!r.ok) { setErr('Please add a title.'); return; }
    onSaved();
  };

  return (
    <section className="rounded-3xl bg-white border border-splash-sand p-5 md:p-6 space-y-4" data-testid="blog-editor">
      <input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Post title" className="input text-lg font-semibold" data-testid="blog-title-input" />
      <input value={form.excerpt} onChange={(e) => set('excerpt', e.target.value)} placeholder="Short summary (shown on the blog list and Google)" className="input" data-testid="blog-excerpt-input" />
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 rounded-full border border-splash-sand px-4 py-2 text-sm font-semibold text-splash-green cursor-pointer">
          {busy === 'upload' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} {form.cover_path ? 'Change cover photo' : 'Upload cover photo'}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="blog-cover-input" />
        </label>
        {form.cover_path && <img src={fileUrl(form.cover_path)} alt="Cover" className="h-16 rounded-xl object-cover" data-testid="blog-cover-preview" />}
      </div>
      <div className="flex flex-wrap items-center gap-1 border-b border-splash-sand pb-2">
        {TOOLS.map((t) => (
          <button key={t.label} type="button" onClick={() => insert(t.wrap)} title={t.label} data-testid={`blog-tool-${t.label.toLowerCase()}`} className="p-2 rounded-lg hover:bg-splash-cream text-splash-green"><t.icon className="w-4 h-4" /></button>
        ))}
        <button type="button" onClick={() => setPreview((v) => !v)} data-testid="blog-preview-toggle" className="ml-auto inline-flex items-center gap-1 text-sm text-splash-teal font-semibold">{preview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />} {preview ? 'Edit' : 'Preview'}</button>
      </div>
      {preview ? (
        <div className="blog-prose min-h-[300px]" data-testid="blog-preview"><ReactMarkdown>{form.body}</ReactMarkdown></div>
      ) : (
        <textarea ref={ta} value={form.body} onChange={(e) => set('body', e.target.value)} rows={16} placeholder="Write your post… Use the toolbar for headings, bold text, lists and links." className="input font-mono text-sm" data-testid="blog-body-input" />
      )}
      {err && <div className="text-sm text-red-600" data-testid="blog-editor-error">{err}</div>}
      <div className="flex flex-wrap gap-3">
        <button type="button" disabled={!!busy} onClick={() => save(true)} data-testid="blog-publish-btn" className="rounded-full bg-splash-coral text-white px-6 py-2.5 font-semibold">Publish</button>
        <button type="button" disabled={!!busy} onClick={() => save(false)} data-testid="blog-draft-btn" className="rounded-full border border-splash-sand px-6 py-2.5 font-semibold text-splash-green">Save draft</button>
        <button type="button" onClick={onCancel} data-testid="blog-cancel-btn" className="text-sm text-splash-green/60">Cancel</button>
      </div>
    </section>
  );
}

function BlogAdmin() {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => adminFetch('/admin/blog').then((r) => r.json()).then((d) => setPosts(d.posts || []));
  useEffect(() => { load(); }, []);
  const del = async (p) => { if (!window.confirm(`Delete "${p.title}"?`)) return; await adminFetch(`/admin/blog/${p.id}`, { method: 'DELETE' }); load(); };

  if (editing) return <Editor post={editing === 'new' ? null : editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;
  return (
    <section className="rounded-3xl bg-white border border-splash-sand p-5" data-testid="admin-blog">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display font-bold text-2xl text-splash-green">Blog posts</h1>
        <button type="button" onClick={() => setEditing('new')} data-testid="blog-new-btn" className="inline-flex items-center gap-1 rounded-full bg-splash-green text-white px-4 py-2 text-sm font-semibold"><Plus className="w-4 h-4" /> New post</button>
      </div>
      <p className="text-xs text-splash-green/60 mb-4">One post a month keeps Google happy. You&rsquo;ll get an email reminder two weeks before each month ends.</p>
      {posts === null ? <Loader2 className="w-5 h-5 animate-spin text-splash-teal" /> : posts.length === 0 ? <div className="text-sm text-splash-green/60">No posts yet.</div> : (
        <ul className="divide-y divide-splash-sand/60">
          {posts.map((p) => (
            <li key={p.id} className="py-3 flex items-center gap-3" data-testid={`admin-blog-row-${p.id}`}>
              <span className={`text-[10px] uppercase tracking-widest font-semibold rounded-full px-2 py-0.5 ${p.published ? 'bg-splash-teal/20 text-splash-tealDark' : 'bg-splash-sand/50 text-splash-green'}`}>{p.published ? 'Live' : 'Draft'}</span>
              <button type="button" onClick={() => setEditing(p)} data-testid={`admin-blog-edit-${p.id}`} className="flex-1 text-left font-semibold text-splash-green hover:text-splash-coral">{p.title}</button>
              {p.published && <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs text-splash-teal">View</a>}
              <button type="button" onClick={() => del(p)} aria-label="Delete" data-testid={`admin-blog-delete-${p.id}`} className="p-1.5 text-splash-green/50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function AdminBlog() {
  return <AdminGate title="Blog Editor"><BlogAdmin /></AdminGate>;
}
